import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';
const SETTINGS_BASE = `${API_BASE}/settings`;

export interface PaymentMethod {
  id: number;
  type: 'BANK_TRANSFER' | 'STRIPE';
  name: string;
  displayName: string | null;
  detailsJson: string | null;
  /** Only ever set for type='STRIPE' — live Stripe Connect status for this specific account. */
  stripeConnected: boolean | null;
  stripeChargesEnabled: boolean | null;
  stripeAccountId: string | null;
  stripePlatformConfigured: boolean | null;
  /** Set only while an owner invitation is outstanding — null once it's used, revoked or expired. */
  stripeInvite: StripeConnectInvite | null;
}

/** An outstanding invitation for the apartment's owner to complete Stripe onboarding themselves,
 * so Stripe collects their bank account rather than the manager's. */
export interface StripeConnectInvite {
  invitedEmail: string;
  expiresAt: string;
  /** Returned so the UI can offer copy-to-clipboard — the only route when mail is disabled. */
  url: string;
  /** The owner has opened the link and started Stripe's form, but hasn't finished it. */
  started: boolean;
}

export interface PaymentMethodInput {
  type: 'BANK_TRANSFER' | 'STRIPE';
  name: string;
  displayName?: string | null;
  detailsJson?: string | null;
}

function getToken(): string {
  return localStorage.getItem('token') || '';
}

function authHeaders() {
  return { Authorization: `Bearer ${getToken()}` };
}

/** `refresh` makes the server re-verify Stripe accounts live instead of trusting a recent stored
 * status — for right after the manager returns from Stripe onboarding. */
export async function listPaymentMethods(opts?: { refresh?: boolean }): Promise<PaymentMethod[]> {
  const res = await axios.get(`${SETTINGS_BASE}/payment-methods`, {
    headers: authHeaders(),
    ...(opts?.refresh ? { params: { refresh: true } } : {}),
  });
  return res.data;
}

export async function createPaymentMethod(data: PaymentMethodInput): Promise<PaymentMethod> {
  const res = await axios.post(`${SETTINGS_BASE}/payment-methods`, data, { headers: authHeaders() });
  return res.data;
}

export async function updatePaymentMethod(id: number, data: PaymentMethodInput): Promise<PaymentMethod> {
  const res = await axios.put(`${SETTINGS_BASE}/payment-methods/${id}`, data, { headers: authHeaders() });
  return res.data;
}

export async function deletePaymentMethod(id: number): Promise<void> {
  await axios.delete(`${SETTINGS_BASE}/payment-methods/${id}`, { headers: authHeaders() });
}
